import { auth, db } from './firebase';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  type User,
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';

// Svelte 5 reactive state using runes
let currentUser = $state<User | null>(null);
let loading = $state(true);
let customClaims = $state<Record<string, any> | null>(null);
let isAccountAdmin = $state(false);
let isSystemAdmin = $state(false);

// Initialize auth state listener with custom claims
onAuthStateChanged(auth, async (user) => {
  currentUser = user;
  if (user) {
    try {
      // Get ID token result to access custom claims
      const tokenResult = await user.getIdTokenResult();
      customClaims = tokenResult.claims;
      // Check for account admin: AccountAdmin claim or account_admin userType in claims
      isAccountAdmin = customClaims?.AccountAdmin === true ||
                       customClaims?.userType === 'account_admin';
      // Check for system admin: isAdmin claim, role === 'admin', or admin_user userType
      isSystemAdmin = customClaims?.isAdmin === true ||
                      customClaims?.role === 'admin' ||
                      customClaims?.userType === 'admin_user';

      // Fallback: If not detected as account admin from claims, check Firestore user document
      // This handles the case where custom claims haven't been set by the backend yet
      if (!isAccountAdmin) {
        try {
          const userDocRef = doc(db, 'users', user.uid);
          const userDoc = await getDoc(userDocRef);
          if (userDoc.exists()) {
            const userData = userDoc.data();
            if (userData?.userType === 'account_admin' || userData?.role === 'accountAdmin') {
              isAccountAdmin = true;
            }
          }
        } catch (firestoreErr) {
          // Silently fail - user doc might not exist or be accessible
          console.warn('Could not check Firestore user document:', firestoreErr);
        }
      }
    } catch (err) {
      console.error('Failed to get custom claims:', err);
      customClaims = null;
      isAccountAdmin = false;
      isSystemAdmin = false;
    }
  } else {
    customClaims = null;
    isAccountAdmin = false;
    isSystemAdmin = false;
  }
  loading = false;
});

export const authState = {
  get user() {
    return currentUser;
  },
  get loading() {
    return loading;
  },
  get isAuthenticated() {
    return currentUser !== null;
  },
  get customClaims() {
    return customClaims;
  },
  get isAccountAdmin() {
    return isAccountAdmin;
  },
  get isSystemAdmin() {
    return isSystemAdmin;
  },
};

/**
 * Force refresh the user's ID token and update custom claims.
 * Call this after claims are expected to have changed (e.g., after registration).
 */
export async function refreshCustomClaims(): Promise<void> {
  const user = auth.currentUser;
  if (!user) return;

  try {
    // Force token refresh
    const tokenResult = await user.getIdTokenResult(true);
    customClaims = tokenResult.claims;
    // Check for account admin: AccountAdmin claim or account_admin userType
    isAccountAdmin = customClaims?.AccountAdmin === true ||
                     customClaims?.userType === 'account_admin';
    // Check for system admin: isAdmin claim, role === 'admin', or admin_user userType
    isSystemAdmin = customClaims?.isAdmin === true ||
                    customClaims?.role === 'admin' ||
                    customClaims?.userType === 'admin_user';

    // Fallback: If not detected as account admin from claims, check Firestore user document
    if (!isAccountAdmin) {
      try {
        const userDocRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          const userData = userDoc.data();
          if (userData?.userType === 'account_admin' || userData?.role === 'accountAdmin') {
            isAccountAdmin = true;
          }
        }
      } catch (firestoreErr) {
        console.warn('Could not check Firestore user document:', firestoreErr);
      }
    }
  } catch (err) {
    console.error('Failed to refresh custom claims:', err);
  }
}

export async function login(email: string, password: string) {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return { success: true, user: userCredential.user };
  } catch (error: any) {
    let errorMessage = error.message;
    if (error.code === 'auth/invalid-credential' || error.code === 'auth/wrong-password' || error.code === 'auth/user-not-found') {
      errorMessage = 'Oops, please check username and password';
    }
    return { success: false, error: errorMessage };
  }
}

export async function signup(email: string, password: string) {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    return { success: true, user: userCredential.user };
  } catch (error: any) {
    return { success: false, error: error.message, errorCode: error.code };
  }
}

export async function logout() {
  try {
    await signOut(auth);
    return { success: true };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
}
